import { useCallback, useMemo, useState } from 'react';
import { canReachStep, type MultiStepMode } from '@org/ui';

/**
 * One rule for every multi-step form in admin:
 * - creating = a real wizard: steps unlock as each one is saved (you may go
 *   back to any step already reached);
 * - editing = free navigation: every step is saved data, click any of them.
 *
 * Leaving a step with unsaved edits is the form's business (it knows what
 * "dirty" means for that step): it calls `requestStep` with a `guard` that
 * returns false to hold the move and show its own unsaved-changes dialog.
 */
export type StepNavigation = {
  mode: MultiStepMode;
  currentIndex: number;
  /** Highest index already unlocked (wizard). */
  reachable: number;
  isLast: boolean;
  canReach: (index: number) => boolean;
  /** Jump to a step the user is allowed to reach. Returns false when refused. */
  goTo: (index: number) => boolean;
  /** Unlock and move to the next step (after the current one was saved). */
  next: () => void;
  back: () => void;
};

export function useStepNavigation(stepCount: number, isEditing: boolean, initialIndex = 0): StepNavigation {
  const mode: MultiStepMode = isEditing ? 'free' : 'wizard';
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [reachable, setReachable] = useState(initialIndex);

  const canReach = useCallback(
    (index: number) => index >= 0 && index < stepCount && canReachStep(index, { mode, currentStep: currentIndex, reachable }),
    [mode, currentIndex, reachable, stepCount],
  );

  const goTo = useCallback(
    (index: number) => {
      if (!canReach(index)) return false;
      setCurrentIndex(index);
      return true;
    },
    [canReach],
  );

  const next = useCallback(() => {
    setCurrentIndex((prev) => {
      const target = Math.min(prev + 1, stepCount - 1);
      setReachable((r) => Math.max(r, target));
      return target;
    });
  }, [stepCount]);

  const back = useCallback(() => setCurrentIndex((prev) => Math.max(prev - 1, 0)), []);

  return useMemo(
    () => ({ mode, currentIndex, reachable, isLast: currentIndex === stepCount - 1, canReach, goTo, next, back }),
    [mode, currentIndex, reachable, stepCount, canReach, goTo, next, back],
  );
}

/** Pure: where a "save then continue" should land (the step the user asked for, else the next one). */
export const resolveAfterSave = (pendingTarget: number | null, currentIndex: number, stepCount: number): number =>
  pendingTarget ?? Math.min(currentIndex + 1, stepCount - 1);
